/**
 * 79. Service Appointments — calendar; technician schedule;
 * completion.
 */
import {
  Card,
  PageHeader,
  Button,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { formatDateTime } from "@/lib/money";

interface AppointmentRow {
  id: string;
  appointmentNumber: string;
  customer: string;
  ticketNumber: string | null;
  technician: string;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  address: string | null;
  status: "Scheduled" | "EnRoute" | "InProgress" | "Completed" | "Cancelled";
}

export function ServiceAppointmentsPage() {
  const { data, isLoading } = useApiList<AppointmentRow>(
    ["service", "appointments"],
    "/service/appointments",
  );

  return (
    <div>
      <PageHeader
        title="Service Appointments"
        subtitle="Calendar, technician schedule, and completion."
        actions={<Button variant="primary">New appointment</Button>}
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Appointments (${data.total})`}>
          <DataTable<AppointmentRow>
            rows={data.items}
            columns={[
              {
                key: "appointmentNumber",
                label: "Appointment",
                render: (r) => (
                  <span className="atlas-mono">{r.appointmentNumber}</span>
                ),
              },
              { key: "customer", label: "Customer" },
              {
                key: "ticketNumber",
                label: "Ticket",
                render: (r) =>
                  r.ticketNumber ? (
                    <span className="atlas-mono">{r.ticketNumber}</span>
                  ) : (
                    "—"
                  ),
              },
              { key: "technician", label: "Technician" },
              {
                key: "scheduledStart",
                label: "Start",
                render: (r) =>
                  r.scheduledStart ? formatDateTime(r.scheduledStart) : "—",
              },
              {
                key: "scheduledEnd",
                label: "End",
                render: (r) =>
                  r.scheduledEnd ? formatDateTime(r.scheduledEnd) : "—",
              },
              {
                key: "address",
                label: "Address",
                render: (r) => r.address ?? "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Completed" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "Cancelled" ? (
                    <Badge tone="danger">{r.status}</Badge>
                  ) : r.status === "InProgress" ? (
                    <Badge tone="primary">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
